// Utility functions for date handling — API returns normalized ISO strings (e.g. "2025-09-01T00:00:00.000Z")

const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

const addMonths = (date, months) => {
    const next = new Date(date);
    next.setMonth(next.getMonth() + months);
    return next;
};

const normalizeToStartOfDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

/**
 * Parse date value to JavaScript Date. Accepts ISO date string or Date instance.
 * @param {string|Date|null|undefined} dateValue - ISO string (e.g. "2025-09-01T00:00:00.000Z") or Date
 * @returns {Date|null}
 */
export const parseMongoDate = (dateValue) => {
    if (dateValue == null) return null;
    if (dateValue instanceof Date) return isNaN(dateValue.getTime()) ? null : dateValue;
    if (typeof dateValue === 'string') {
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    }
    if (typeof dateValue === 'number') {
        const date = new Date(dateValue);
        return isNaN(date.getTime()) ? null : date;
    }
    return null;
};

/**
 * Split a date range into evenly sized rotation segments.
 * The range is inclusive on both ends.
 */
export const splitDateRangeIntoRotations = (startDateValue, endDateValue, rotationCount = 6) => {
    const safeRotationCount = Math.max(1, Number(rotationCount) || 1);
    const startDate = parseMongoDate(startDateValue);
    const endDate = parseMongoDate(endDateValue);

    if (!startDate || !endDate) {
        return Array.from({ length: safeRotationCount }, () => ({ startDate: '', endDate: '' }));
    }

    const rangeStart = normalizeToStartOfDay(startDate);
    const rangeEnd = normalizeToStartOfDay(endDate);
    if (rangeStart > rangeEnd) {
        return Array.from({ length: safeRotationCount }, () => ({ startDate: '', endDate: '' }));
    }

    const totalDays = Math.floor((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24)) + 1;
    const rotations = Array.from({ length: safeRotationCount }, (_, index) => {
        const startOffset = Math.floor((index * totalDays) / safeRotationCount);
        const endOffset = index === safeRotationCount - 1
            ? totalDays - 1
            : Math.floor(((index + 1) * totalDays) / safeRotationCount) - 1;

        const rotationStart = addDays(rangeStart, startOffset);
        const rotationEnd = addDays(rangeStart, Math.max(startOffset, endOffset));

        return {
            startDate: toInputDate(rotationStart),
            endDate: toInputDate(rotationEnd)
        };
    });

    return rotations;
};

/**
 * Find the overall date span covered by an ordered rotation list.
 */
export const getRotationRangeFromRotations = (rotations = []) => {
    const validRotations = Array.isArray(rotations)
        ? rotations
            .map((rotation) => ({
                startDate: parseMongoDate(rotation?.startDate),
                endDate: parseMongoDate(rotation?.endDate)
            }))
            .filter((rotation) => rotation.startDate && rotation.endDate)
        : [];

    if (!validRotations.length) {
        return null;
    }

    const startDate = validRotations.reduce(
        (earliest, rotation) => (rotation.startDate < earliest ? rotation.startDate : earliest),
        validRotations[0].startDate
    );
    const endDate = validRotations.reduce(
        (latest, rotation) => (rotation.endDate > latest ? rotation.endDate : latest),
        validRotations[0].endDate
    );

    return { startDate, endDate };
};

/**
 * Build near-future default curriculum dates.
 * The schedule starts tomorrow, keeps registration before the first rotation,
 * and keeps the last rotation within roughly one academic year.
 */
export const buildCurriculumDateDefaults = (rotationCount = 6) => {
    const safeRotationCount = Math.max(1, Number(rotationCount) || 6);
    const startDate = addDays(new Date(), 1);
    const registrationEndDate = addDays(startDate, 13);
    const firstRotationStartDate = addDays(registrationEndDate, 1);
    const rotationEndDate = addDays(addMonths(firstRotationStartDate, 10), -1);
    const rotations = splitDateRangeIntoRotations(firstRotationStartDate, rotationEndDate, safeRotationCount);

    const lastRotationEnd = rotations[rotations.length - 1]?.endDate || toInputDate(firstRotationStartDate);

    return {
        curriculumStartDate: toInputDate(startDate),
        curriculumEndDate: lastRotationEnd,
        formRegistration: {
            startDate: toInputDate(startDate),
            endDate: toInputDate(registrationEndDate)
        },
        rotations
    };
};

/**
 * Format date for display (uses parseMongoDate for consistency).
 */
export const formatMongoDate = (dateValue) => {
    const date = parseMongoDate(dateValue);
    return date ? date.toLocaleDateString() : 'Invalid Date';
};

/**
 * Format date for display with time.
 */
export const formatMongoDateTime = (dateValue) => {
    const date = parseMongoDate(dateValue);
    return date ? date.toLocaleString() : 'Invalid Date';
};

/**
 * Format date for input fields (YYYY-MM-DD).
 */
export const formatMongoDateForInput = (dateValue) => {
    const date = parseMongoDate(dateValue);
    return date ? date.toISOString().split('T')[0] : '';
};
